import { useEffect, useState } from "react";
import styles from "./EnterAddress.module.scss";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";

// components
import MainHeader from "../../components/Header/MainHeader";
import DataSafetyMessage from "../../components/Messages/DataSafetyMessage";

// methods
import ServicesAPI from "../../services/services";
import { currentAddressValidation } from "../../validations/user.validation";
import { useDispatch, useSelector } from "react-redux";
import { addUserName } from "../../redux/slices/userSlice";
import { CustomButton } from "../../components";
import { googleEvent_LOJ085, googleEvent_LOJ226 } from "../../lib/google-analytics";
import { googleEvent_LOJ082 } from "../../lib/google-analytics-loan-journey-events";

export default function EnterAddress() {
  const [address, setAddress] = useState(null);
  const [searchState, setSearchState] = useState("");
  const [stateLists, setStateLists] = useState([]);

  // const { handleStep } = useResponse();
  const { repeatCustomer } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentAddressUpdate } = new ServicesAPI();

  // console.log("repeatCustomer---------->>>>", repeatCustomer);

  useEffect(() => {
    const fetchAddress = async () => {
      const { getAddressAPI } = new ServicesAPI();
      const res = await getAddressAPI();
      if (res && res.statusCode === 200) {
        setAddress(res.data);
        dispatch(addUserName(res.data.name));
      }
    };

    fetchAddress();
    googleEvent_LOJ082();
  }, [dispatch]);

  useEffect(() => {
    const handleSearch = setTimeout(() => {
      if (searchState) {
        const handleGetStates = async () => {
          const { getStates } = new ServicesAPI();
          const res = await getStates(searchState);
          if (res && res.statusCode === 200) {
            setStateLists(res.data);
          }
        };
        handleGetStates();
      } else {
        setStateLists([]);
      }
    }, 300);

    return () => clearTimeout(handleSearch);
  }, [searchState]);

  const initialValues = {
    houseNo: address?.address || "",
    city: address?.city || "",
    state: address?.state || "",
    pinCode: address?.pincode || "",
    landmark: address?.landmark || "",
  };

  return (
    <div className="wrapper auto-height">
      <Formik
        initialValues={initialValues}
        validationSchema={currentAddressValidation}
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);

          setTimeout(async () => {
            const res = await currentAddressUpdate(values, repeatCustomer);

            if (res && res.statusCode === 200) {
              if (repeatCustomer) {
                navigate("/confirm-details");
                return;
              }

              const message = res?.data?.CibilAndBreData?.data?.data?.message;
              const status = res?.data?.CibilAndBreData?.data?.data?.status;

              if (status === 1 && message) {
                switch (message) {
                  case "Approved":
                    navigate("/select-loan");
                    break;

                  case "Proceed to Bank":
                    googleEvent_LOJ226();
                    navigate(`/finbox?url=${res?.data?.finboxRedirectUrl}`);
                    break;

                  case "BRE Accepted":
                    navigate(`/select-loan`);
                    break;

                  case "Reject":
                    navigate(`/loan-reject`);
                    break;

                  default:
                    navigate("/select-loan");
                    break;
                }
              } else if (status === 1) {
                navigate(`/select-loan`);
              } else if (status !== 1 && (message === "Cibil Rejected" || message === "Dtree Bre Rejected")) {
                navigate("/loan-reject");
              } else if (!status) {
                googleEvent_LOJ226();
                navigate(`/finbox?url=${res?.data?.finboxRedirectUrl}`);
              }
            } else if (res.statusCode === 406) {
              navigate(`${res?.data?.route}`);
            }

            // Events
            googleEvent_LOJ085();

            setSubmitting(false);
          }, 500);
        }}
      >
        {({ errors, values, touched, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit} style={{ height: "100%" }}>
            <div className="vertical-column-layout">
              <div className="vertical-column-top">
                <MainHeader title="Residential Details" showProgress={true} percent={100} backPageName={repeatCustomer ? "/confirm-details" : ""} />
                <p className="label">Enter your Current Address</p>
                <p className="sub-label mb-3">Is your current address same as aadhar?</p>

                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Flat / House no. / Floor / Building"
                    name="houseNo"
                    value={values.houseNo}
                    onChange={handleChange}
                    onBlur={(e) => {
                      setFieldValue("houseNo", e.target.value.trim());
                    }}
                    autoComplete="off"
                  />

                  {touched.houseNo && errors.houseNo ? <div className="error-msg">{errors.houseNo}</div> : null}
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="City"
                    name="city"
                    value={values.city}
                    onChange={handleChange}
                    onBlur={(e) => {
                      setFieldValue("city", e.target.value.trim());
                    }}
                    autoComplete="off"
                  />

                  {touched.city && errors.city ? <div className="error-msg">{errors.city}</div> : null}
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    className={`form-control ${stateLists.length > 0 ? styles.dropdown : ""}`}
                    placeholder="State"
                    name="state"
                    value={values.state}
                    onChange={(e) => {
                      setSearchState(e.target.value);
                      handleChange(e);
                      setFieldValue("state", e.target.value);
                    }}
                    onBlur={handleBlur}
                    autoComplete="off"
                  />

                  {touched.state && errors.state ? <div className="error-msg">{errors.state}</div> : null}

                  {stateLists.length > 0 && (
                    <div className={styles.autoComplete}>
                      <ul>
                        {stateLists.map((item, index) => (
                          <li
                            key={index}
                            onClick={() => {
                              setFieldValue("state", item);
                              setStateLists([]);
                            }}
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Pincode"
                    name="pinCode"
                    value={values.pinCode}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (/^\d*$/.test(input)) {
                        setFieldValue("pinCode", e.target.value);
                      }
                    }}
                    onBlur={(e) => {
                      setFieldValue("pinCode", e.target.value.trim());
                    }}
                    autoComplete="off"
                    maxLength={6}
                  />

                  {touched.pinCode && errors.pinCode ? <div className="error-msg">{errors.pinCode}</div> : null}
                </div>

                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nearby Landmark"
                    name="landmark"
                    value={values.landmark}
                    onChange={handleChange}
                    onBlur={(e) => {
                      setFieldValue("landmark", e.target.value.trim());
                    }}
                    autoComplete="off"
                  />

                  {touched.landmark && errors.landmark ? <div className="error-msg">{errors.landmark}</div> : null}
                </div>
              </div>

              <div className="vertical-column-bottom">
                <DataSafetyMessage />
                <CustomButton text="Next" type="submit" />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}
